/**
 * Course Interest API Service
 * Client for communicating with the Course Interest endpoints in Payment Service
 * 
 * Requirements: 7.1-7.7, 8.1-8.6, 9.1-9.5, 14.1, 14.7
 */

/**
 * Payment Service base URL from environment variable
 */
const PAYMENT_API_URL = import.meta.env.VITE_PAYMENT_API_URL || 'http://localhost:3001';

/**
 * Default timeout for API requests (15 seconds)
 */
const DEFAULT_TIMEOUT = 15000;

/**
 * LocalStorage key for admin JWT token
 */
const TOKEN_STORAGE_KEY = 'admin_token';

/**
 * Creates an AbortController with timeout
 */
function createTimeoutController(timeout) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
}

/**
 * Handles API response and extracts JSON data
 */
async function handleResponse(response) {
  const data = await response.json();
  
  if (!response.ok) {
    const errorMessage = data.error || data.errorMessage || 'Error en el servidor';
    const error = new Error(errorMessage);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  
  return data;
}

/**
 * Gets the stored admin token from localStorage
 */
export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Stores the admin token in localStorage
 */
export function storeToken(token) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch (error) {
    console.error('Error storing token:', error);
  }
}

/**
 * Removes the admin token from localStorage
 */
export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing token:', error);
  }
}

/**
 * Submit interest in a presential course (public endpoint)
 * Requirements: 7.1, 7.2, 7.3, 7.4
 * 
 * @param {Object} data - Interest registration data
 * @param {number} data.courseId - Course ID
 * @param {string} data.courseName - Course name
 * @param {string} data.nombre - Full name
 * @param {string} data.email - Email address
 * @param {string} data.rut - Chilean RUT (formatted: XX.XXX.XXX-X)
 * @param {string} data.telefono - Phone number (Chilean format: +56 9 XXXX XXXX)
 * @param {boolean} data.consent - Data processing consent
 * @param {string} data.recaptchaToken - reCAPTCHA v2 token
 * @param {number} data.formOpenedAt - Timestamp when form was opened
 * @param {string} [data.honeypot] - Honeypot field (should be empty)
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - Interest registration response
 * @returns {boolean} return.success - Whether registration was successful
 * @returns {string} return.message - Status message
 * @returns {string} [return.interestId] - Created interest record ID
 * @returns {boolean} [return.isDuplicate] - True if duplicate registration
 */
export async function submitInterest(data, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const { controller, timeoutId } = createTimeoutController(timeout);

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/course-interest`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera. Por favor intenta nuevamente.');
    }

    if (error.status === 409) {
      const err = new Error(error.message || 'Ya registramos tu interés en este curso. Te contactaremos pronto.');
      err.status = 409;
      err.isDuplicate = true;
      throw err;
    }

    if (error.status === 429) {
      throw new Error('Demasiadas solicitudes. Por favor, espera un momento.');
    }

    if (error.status === 400) {
      throw error;
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Get all interest records with optional filters
 * Requirements: 7.5, 7.6, 8.1, 8.2, 8.3
 * 
 * @param {Object} filters - Optional filters
 * @param {number} filters.courseId - Filter by course ID
 * @param {string} filters.status - Filter by status (pending, confirmed, cancelled)
 * @param {boolean} filters.notified - Filter by notification status
 * @param {number} filters.groupNumber - Filter by group number
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - Interest records and summary
 */
export async function getInterests(filters = {}, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const token = options.token || getStoredToken();
  const { controller, timeoutId } = createTimeoutController(timeout);

  if (!token) {
    throw new Error('Debes iniciar sesión como administrador.');
  }

  try {
    const params = new URLSearchParams();
    if (filters.courseId) params.append('courseId', filters.courseId);
    if (filters.status) params.append('status', filters.status);
    if (filters.notified !== undefined) params.append('notified', filters.notified);
    if (filters.groupNumber) params.append('groupNumber', filters.groupNumber);
    if (filters.email) params.append('email', filters.email);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);

    const url = `${PAYMENT_API_URL}/api/admin/course-interest${params.toString() ? `?${params}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }

    if (error.status === 401) {
      clearToken();
      throw new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Get a single interest record by ID
 * 
 * @param {string} id - Interest record ID
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - Interest record
 */
export async function getInterestById(id, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const token = options.token || getStoredToken();
  const { controller, timeoutId } = createTimeoutController(timeout);

  if (!token) {
    throw new Error('Debes iniciar sesión como administrador.');
  }

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/admin/course-interest/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }

    if (error.status === 401) {
      clearToken();
      throw new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    }

    if (error.status === 404) {
      throw new Error('Registro de interés no encontrado.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Update interest record status
 * Requirements: 9.1, 9.2, 9.3, 9.4
 * 
 * @param {string} id - Interest record ID
 * @param {string} status - New status (pending, confirmed, cancelled)
 * @param {string} notes - Optional notes
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - Updated interest record
 */
export async function updateInterestStatus(id, status, notes = '', options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const token = options.token || getStoredToken();
  const { controller, timeoutId } = createTimeoutController(timeout);

  if (!token) {
    throw new Error('Debes iniciar sesión como administrador.');
  }

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/admin/course-interest/${encodeURIComponent(id)}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ status, notes }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }

    if (error.status === 401) {
      clearToken();
      throw new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    }

    if (error.status === 404) {
      throw new Error('Registro de interés no encontrado.');
    }

    if (error.status === 400) {
      throw new Error('Estado inválido. Debe ser: pending, confirmed, o cancelled.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Delete interest record (soft delete)
 * Requirements: 13.3, 13.4
 * 
 * @param {string} id - Interest record ID
 * @param {string} reason - Reason for deletion
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - Deletion result
 */
export async function deleteInterest(id, reason, options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const token = options.token || getStoredToken();
  const { controller, timeoutId } = createTimeoutController(timeout);

  if (!token) {
    throw new Error('Debes iniciar sesión como administrador.');
  }

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/admin/course-interest/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ reason }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }

    if (error.status === 401) {
      clearToken();
      throw new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    }

    if (error.status === 404) {
      throw new Error('Registro de interés no encontrado.');
    }

    if (error.status === 400) {
      throw new Error('Debe proporcionar una razón para la eliminación.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Export interest records as CSV
 * Requirements: 8.6
 * 
 * @param {Object} filters - Optional filters
 * @param {Object} options - Request options
 * @returns {Promise<void>} - Triggers file download
 */
export async function exportInterestsCSV(filters = {}, options = {}) {
  const timeout = options.timeout || 30000;
  const token = options.token || getStoredToken();
  const { controller, timeoutId } = createTimeoutController(timeout);

  if (!token) {
    throw new Error('Debes iniciar sesión como administrador.');
  }

  try {
    const params = new URLSearchParams();
    if (filters.courseId) params.append('courseId', filters.courseId);
    if (filters.status) params.append('status', filters.status);

    const url = `${PAYMENT_API_URL}/api/admin/course-interest/export${params.toString() ? `?${params}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      try {
        const errorData = await response.json();
        const error = new Error(errorData.error || 'Error al exportar');
        error.status = response.status;
        throw error;
      } catch (parseError) {
        if (parseError.status) throw parseError;
        const error = new Error('Error al exportar el archivo');
        error.status = response.status;
        throw error;
      }
    }

    const blob = await response.blob();
    const filename = options.filename || `intereses-cursos-${new Date().toISOString().split('T')[0]}.csv`;

    const url2 = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url2;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url2);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La descarga ha excedido el tiempo de espera.');
    }

    if (error.status === 401) {
      clearToken();
      throw new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

/**
 * Send payment links to confirmed group
 * Requirements: 14.1, 14.2, 14.3, 14.4, 14.5
 * 
 * @param {number} courseId - Course ID
 * @param {number} groupNumber - Group number
 * @param {number} coursePrice - Course price in CLP
 * @param {string} courseDate - Optional course date
 * @param {string} courseLocation - Optional course location
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - Result with sent links
 */
export async function sendPaymentLinks(courseId, groupNumber, coursePrice, courseDate = '', courseLocation = '', options = {}) {
  const timeout = options.timeout || DEFAULT_TIMEOUT;
  const token = options.token || getStoredToken();
  const { controller, timeoutId } = createTimeoutController(timeout);

  if (!token) {
    throw new Error('Debes iniciar sesión como administrador.');
  }

  try {
    const response = await fetch(`${PAYMENT_API_URL}/api/admin/course-interest/send-payment-links`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ 
        courseId, 
        groupNumber, 
        coursePrice,
        courseDate,
        courseLocation
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return await handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error('La solicitud ha excedido el tiempo de espera.');
    }

    if (error.status === 401) {
      clearToken();
      throw new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    }

    if (error.status === 404) {
      throw new Error('No se encontraron estudiantes en este grupo.');
    }

    if (error.status === 400) {
      throw new Error('Faltan campos requeridos: courseId, groupNumber, coursePrice.');
    }

    if (error.status) {
      throw error;
    }

    throw new Error('Error de conexión. Por favor verifica tu conexión a internet.');
  }
}

export default {
  submitInterest,
  getInterests,
  getInterestById,
  updateInterestStatus,
  deleteInterest,
  exportInterestsCSV,
  sendPaymentLinks,
  getStoredToken,
  storeToken,
  clearToken,
};
